import React, { useEffect, useMemo, useRef, useState } from "react";

import { formatNumber, productAnalytics, useGetFarms, useGetFieldsByFarmId, useNavigate, useTranslation, useUserContext } from "utils";

import { SCENARIO_LIMITS } from "consts";

import { FieldListField } from "models";

import { AlertBanner, Collapse, FarmFieldSelector, Icon, Typography } from "components";

import { CropRotationTable } from "pages/app/scenario-analysis/components/crop-rotation/CropRotationTable";
import { useMainCropOptions } from "pages/app/scenario-analysis/hooks/useMainCropOptions";
import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { createEmptyYear, cropYearHasAnyEntry, reconcileYearsResidueLocks } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { ImportBanner } from "pages/app/scenario-onboarding/components/ImportBanner";
import { OnboardingLayout } from "pages/app/scenario-onboarding/components/OnboardingLayout";
import { useBaselineDraftAutosave } from "pages/app/scenario-onboarding/hooks/useBaselineDraftAutosave";
import { useSoilSightOnboardingSettingPatch } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { useSubmitBaselineAndGenerate } from "pages/app/scenario-onboarding/hooks/useSubmitBaselineAndGenerate";
import { validateBaselineCropYears } from "pages/app/scenario-onboarding/utils/baselineValidation";
import { applyPracticeDefaults } from "pages/app/scenario-onboarding/utils/practiceDefaults";
import { isPracticeInRows } from "pages/app/scenario-onboarding/utils/recommendationState";

const FIELD_PANEL_KEY = "field";
const ROTATION_PANEL_KEY = "rotation";

export const Step1BaselinePage = () => {
  const { t } = useTranslation(["scenarioAnalysis", "global"]);
  const navigate = useNavigate();
  const { user, switchFarm, reloadUserData } = useUserContext();
  const {
    selectedFarmId,
    selectedFieldId,
    setSelectedFarmId,
    setSelectedFieldId,
    cropYears: years,
    setCropYears: setYears,
    importMeta,
    setImportMeta,
    validationAttempted,
    setValidationAttempted,
  } = useScenarioOnboarding();

  useBaselineDraftAutosave();
  const { submitBaseline, isSubmitting } = useSubmitBaselineAndGenerate();
  const { patchImportBannerDismissed } = useSoilSightOnboardingSettingPatch();

  const [activePanel, setActivePanel] = useState<string>(FIELD_PANEL_KEY);
  const hasAutoAdvancedRef = useRef(false);
  const [addYearIssue, setAddYearIssue] = useState<string[]>([]);
  const [addedFieldId, setAddedFieldId] = useState<string | null>(null);

  const { mainCropOptions, isPracticeCropsFetching } = useMainCropOptions(years);
  const { data: fields = [] } = useGetFieldsByFarmId({ farmId: selectedFarmId ?? "", enabled: !!selectedFarmId });
  const { currentFarm } = useGetFarms();

  // Once a field is picked (incl. auto-selection), advance to the rotation panel.
  useEffect(() => {
    if (selectedFieldId && !hasAutoAdvancedRef.current) {
      hasAutoAdvancedRef.current = true;
      setActivePanel(ROTATION_PANEL_KEY);
    }
  }, [selectedFieldId]);

  const validation = useMemo(() => validateBaselineCropYears(years), [years]);

  const selectedField = useMemo(() => fields.find((field: FieldListField) => field.id === selectedFieldId) ?? null, [fields, selectedFieldId]);
  const fieldSummary = selectedField
    ? [selectedField.name, currentFarm?.region || null, selectedField.size > 0 ? `${formatNumber({ number: selectedField.size, decimalPlaces: 2 })} ha` : null]
        .filter(Boolean)
        .join(" · ")
    : "";

  const handleYearDataChange = (yearId: string, updates: Partial<CropYear>) => {
    setYears((previous) => {
      const next = previous.map((year) => {
        if (year.id !== yearId) return year;

        if ("crop" in updates && !updates.crop?.trim()) {
          return { ...year, ...createEmptyYear(year.id), legumeShare: null, ccCost: null };
        }

        // A manually added crop prefills the practice defaults (residue removed,
        // conventional tillage, no cover crop) — the user can change them afterwards.
        if ("crop" in updates && updates.crop?.trim()) {
          return applyPracticeDefaults({ ...year, ...updates });
        }

        return { ...year, ...updates };
      });
      return reconcileYearsResidueLocks(next, mainCropOptions);
    });

    if ("crop" in updates && updates.crop) {
      setAddYearIssue((previous) => previous.filter((id) => id !== yearId));
    }
  };

  const handleYearRemove = (yearId: string) => {
    setYears((previous) => {
      const index = previous.findIndex((year) => year.id === yearId);
      if (index === -1) return previous;
      const year = previous[index];
      const hasAnyEntry = cropYearHasAnyEntry(year);
      if (previous.length > SCENARIO_LIMITS.MIN_YEARS) {
        return previous.filter((candidate) => candidate.id !== yearId);
      }
      if (hasAnyEntry) {
        return previous.map((candidate) => (candidate.id === yearId ? createEmptyYear(candidate.id) : candidate));
      }
      return previous;
    });
  };

  const handleYearReorder = (fromIndex: number, toIndex: number) => {
    setYears((previous) => {
      const next = [...previous];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  // The flow reads user.farm for every farm-scoped lookup, so the selection has to become the
  // platform-active farm — switchFarm + reloadUserData, as the other farm switchers do.
  const handleFarmSelected = (farmId: string | null) => {
    setSelectedFarmId(farmId);
    if (farmId && farmId !== user?.farm?.id) {
      switchFarm(farmId);
      reloadUserData();
    }
  };

  const handleAddYear = () => {
    const incomplete = years.filter((year) => !year.crop?.trim()).map((year) => year.id);
    if (incomplete.length > 0) {
      setAddYearIssue(incomplete);
      return;
    }
    setAddYearIssue([]);
    setYears((previous) => [...previous, createEmptyYear(`onboarding-y-${Date.now()}`)]);
  };

  const handleDismissImportBanner = () => {
    setImportMeta((previous) => (previous ? { ...previous, bannerDismissed: true } : previous));
    patchImportBannerDismissed();
  };

  const handleCalculateForecast = async () => {
    // Surface the incomplete section: open the collapse the error lives in, even if closed.
    if (!selectedFieldId) {
      productAnalytics.track("soilsights_onboarding:baseline_validation_fail", { reason: "no_field" });
      setValidationAttempted(true);
      setActivePanel(FIELD_PANEL_KEY);
      return;
    }
    if (!validation.valid) {
      productAnalytics.track("soilsights_onboarding:baseline_validation_fail", { reason: "invalid_rotation" });
      setValidationAttempted(true);
      setActivePanel(ROTATION_PANEL_KEY);
      return;
    }

    const getFieldSource = () => {
      if (importMeta?.importedFieldId === selectedFieldId) return "field_import";
      return addedFieldId === selectedFieldId ? "new" : "existing";
    };
    productAnalytics.track("soilsights_onboarding:baseline_submit", {
      years_count: years.filter((year) => year.crop?.trim()).length,
      field_source: getFieldSource(),
      has_cover_crop: isPracticeInRows(years, "coverCropId"),
    });

    // The gates read user.farm — keep the platform-active farm in sync with the selection.
    if (selectedFarmId && selectedFarmId !== user?.farm?.id) {
      switchFarm(selectedFarmId);
    }
    await submitBaseline();
  };

  // Error highlighting only after a submit attempt.
  const tillageErrorYearIds = validationAttempted
    ? Object.keys(validation.errorsByYearId).filter((id) => validation.errorsByYearId[id].includes("tillageId"))
    : [];
  const residueErrorYearIds = validationAttempted
    ? Object.keys(validation.errorsByYearId).filter((id) => validation.errorsByYearId[id].includes("residue"))
    : [];
  const missingCropYearIds = validationAttempted ? Object.keys(validation.errorsByYearId).filter((id) => validation.errorsByYearId[id].includes("crop")) : [];

  const validationErrorMessage =
    validationAttempted && !validation.valid
      ? t(validation.hasAnyCrop ? "scenarioAnalysis:onboarding.step1.validationError" : "scenarioAnalysis:onboarding.step1.emptyStateError")
      : undefined;

  const addYearErrorMessage = addYearIssue.length > 0 ? t("scenarioAnalysis:scenarioBuilder.addYearValidationMessage") : undefined;

  const hasUnsupportedCrops = years.some((year) => year.hasUnresolvableCrop) || (importMeta?.droppedMainCrops.length ?? 0) > 0;

  const panelHeader = (stepNumber: number, isDone: boolean, title: string, summary?: string) => (
    <div className="onboarding-collapse__header">
      {isDone ? (
        <Icon iconName="tabler/circle-check" size="m" className="onboarding-collapse__check" />
      ) : (
        <span className="onboarding-collapse__num">{stepNumber}</span>
      )}
      <Typography variant="paragraph-m" className="onboarding-collapse__title">
        {title}
      </Typography>
      {summary && (
        <Typography variant="paragraph-s" color="grey-600" className="onboarding-collapse__summary">
          {summary}
        </Typography>
      )}
    </div>
  );

  const collapseItems = [
    {
      key: FIELD_PANEL_KEY,
      className: "onboarding-collapse__field",
      label: panelHeader(1, Boolean(selectedFieldId), t("scenarioAnalysis:onboarding.step1.yourField"), fieldSummary),
      children: (
        <div className="onboarding-collapse__field-selectors">
          <FarmFieldSelector
            selectedFarmId={selectedFarmId}
            selectedFieldId={selectedFieldId}
            setSelectedFarmId={handleFarmSelected}
            setSelectedFieldId={setSelectedFieldId}
            onFieldAdded={setAddedFieldId}
          />
        </div>
      ),
    },
    {
      key: ROTATION_PANEL_KEY,
      className: "onboarding-collapse__rotation",
      label: panelHeader(
        2,
        validation.valid,
        t("scenarioAnalysis:onboarding.step1.yourRotation"),
        !selectedFieldId ? t("scenarioAnalysis:onboarding.step1.addFieldFirst") : undefined
      ),
      collapsible: selectedFieldId ? undefined : ("disabled" as const),
      children: (
        <CropRotationTable
          variant="onboarding"
          years={years}
          mainCropOptions={mainCropOptions}
          isMainCropOptionsLoading={isPracticeCropsFetching}
          onYearDataChange={handleYearDataChange}
          onYearRemove={handleYearRemove}
          onYearReorder={handleYearReorder}
          hideRefineAction
          onAddYear={handleAddYear}
          invalidYearIds={[...addYearIssue, ...missingCropYearIds]}
          tillageErrorYearIds={tillageErrorYearIds}
          residueErrorYearIds={residueErrorYearIds}
          addYearErrorMessage={addYearErrorMessage}
          validationErrorMessage={validationErrorMessage}
          maxYears={SCENARIO_LIMITS.MAX_YEARS}
          dataTestId="onboarding-crop-table"
        />
      ),
    },
  ];

  const isIncomplete = !selectedFieldId || !validation.valid;
  const footerHint = isIncomplete ? t("scenarioAnalysis:onboarding.step1.footerHint") : undefined;

  // The intro copy follows progress: field selection first, then the rotation focus.
  const hasCompletedFieldSection = Boolean(selectedFieldId);

  return (
    <OnboardingLayout
      stepIndex={0}
      pageName="scenario-onboarding-step-1"
      title={t(hasCompletedFieldSection ? "scenarioAnalysis:onboarding.step1.rotationHeading" : "scenarioAnalysis:onboarding.step1.heading")}
      subtitle={t(hasCompletedFieldSection ? "scenarioAnalysis:onboarding.step1.rotationSubheading" : "scenarioAnalysis:onboarding.step1.subheading")}
      wideSubtitle
      footer={{
        onBack: () => navigate("/scenarios-landing-page", { state: { fromOnboarding: true } }),
        hint: footerHint,
        hintIsError: isIncomplete && validationAttempted,
        ctaLabel: t("scenarioAnalysis:onboarding.step1.calculateCta"),
        onCtaClick: handleCalculateForecast,
        ctaDisabled: isSubmitting,
        ctaLoading: isSubmitting,
        ctaTestId: "onboarding-calculate-btn",
      }}
    >
      <div className="onboarding-step__content">
        {/* One import banner at a time: unsupported crops take priority over the success note. */}
        {importMeta && !importMeta.bannerDismissed && !hasUnsupportedCrops && (
          <ImportBanner rotationImported={importMeta.rotationImported} onDismiss={handleDismissImportBanner} />
        )}
        {hasUnsupportedCrops && (
          <AlertBanner
            dataTestId="onboarding-unsupported-crops"
            type="error"
            iconName="tabler/info-circle"
            iconAlign="start"
            closeable
            description={t("scenarioAnalysis:onboarding.step1.unsupportedCrops")}
          />
        )}

        <Collapse
          className="onboarding-collapse"
          activeKey={activePanel ? [activePanel] : []}
          onChange={(keys) => {
            const next = Array.isArray(keys) ? keys[keys.length - 1] : keys;
            setActivePanel(typeof next === "string" ? next : "");
          }}
          items={collapseItems}
        />
      </div>
    </OnboardingLayout>
  );
};
